import React, { ForwardedRef, MouseEventHandler, ReactElement, ReactNode, memo } from 'react';
import cn from 'classnames';
import { Tooltip } from '@material-ui/core';

import BrIcons from '@components/BrIcons';
import BrText from '@components/BrText';
import ColorStyles from '@components/ColorStyles';
import './brButton.scss';

interface BrButtonProps {
  children?: ReactNode;
  className?: string;
  colorStyle?: string;
  dataAttributes?: Record<string, string>; // Object containing all 'data-' attributes
  icon?: ReactElement;
  isDisabled?: boolean;
  isLoading?: boolean;
  isShort?: boolean;
  isSubComp?: boolean;
  noEdge?: boolean;
  onClick: MouseEventHandler<HTMLElement>
  title: ReactNode;
  tooltipText?: string | NonNullable<ReactNode>;
}

const BrButton = React.forwardRef<HTMLDivElement, BrButtonProps>(
  (props: BrButtonProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      children,
      className,
      colorStyle = ColorStyles.GRAY,
      dataAttributes,
      icon,
      isDisabled,
      isLoading,
      isShort,
      isSubComp,
      noEdge,
      onClick,
      title,
      tooltipText,
    } = props;

    const hasChildren = !!children;

    const btnClass = cn('br-button', className, colorStyle, {
      loading: isLoading,
      short: isShort,
      'no-edge': noEdge,
      disabled: isDisabled,
      'sub-comp': isSubComp,
      'custom-component': hasChildren,
    });

    const dataSet = Object.entries(dataAttributes || {}).reduce(
      (acc, [key, value]) => {
        return /data-\S+/gi.test(key) ? { ...acc, [key]: value } : acc;
      },
      {},
    );

    const handleOnClick = isDisabled || isLoading ? undefined : onClick;

    let renderContent;
    if (hasChildren) {
      renderContent = children;
    } else {
      renderContent = typeof title === 'string' ? <BrText>{title}</BrText> : title;
    }

    const buttonWithoutHover = (
      <div
        className={btnClass}
        {...dataSet}
        onClick={handleOnClick}
        data-id="br-button"
        ref={ref}
      >
        {icon && <i className="prefix-icon">{icon}</i>}
        {isLoading ? <BrIcons.Spinner isSubComp /> : renderContent}
      </div>
    );

    const buttonWithHover = (text: string | NonNullable<ReactNode>) => (
      <Tooltip arrow title={text}>
        <span>{buttonWithoutHover}</span>
      </Tooltip>
    );

    return tooltipText ? buttonWithHover(tooltipText) : buttonWithoutHover;
  },
);

BrButton.displayName = 'BrButton';
export default memo(BrButton);
